import { cn } from "@/lib/utils";
import { 
  TrendingUp, 
  Newspaper, 
  Users, 
  Calendar, 
  BookOpen,
  Globe,
  Brain
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import type { ProductBrand } from "@shared/schema";
import { getInjectedBrandLogo } from "@/lib/brandLogos";

export type ProductLine = "global" | "capital" | "media" | "network" | "summit" | "book" | "chiefinai";

function extractHexFromGradient(gradient: string): { from: string; to: string; midpoint: number } {
  const fromMatch = gradient.match(/from-\[([#\w]+)\]/);
  const toMatch = gradient.match(/to-\[([#\w]+)\]/);
  const midpointMatch = gradient.match(/via-\[([#\w]+)\]\s+via-(\d+)%/);
  const fromPctMatch = gradient.match(/from-(\d+)%/);
  
  let midpoint = 50;
  if (midpointMatch) {
    midpoint = parseInt(midpointMatch[2], 10);
  } else if (fromPctMatch) {
    midpoint = parseInt(fromPctMatch[1], 10);
  }
  
  return {
    from: fromMatch ? fromMatch[1] : "#4C1481",
    to: toMatch ? toMatch[1] : "#14B8A6",
    midpoint,
  };
}

function gradientToInlineStyle(gradient: string): React.CSSProperties {
  const { from, to, midpoint } = extractHexFromGradient(gradient);
  if (midpoint === 50) {
    return { background: `linear-gradient(to bottom right, ${from}, ${to})` };
  }
  return { background: `linear-gradient(to bottom right, ${from} 0%, ${from} ${midpoint}%, ${to} 100%)` };
}

interface ProductLogoProps {
  product: ProductLine;
  variant?: "full" | "icon" | "compact";
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}

const productConfig: Record<ProductLine, {
  name: string;
  tagline: string;
  icon: typeof TrendingUp;
  gradient: string;
  accentColor: string;
  fallbackIcon: string;
}> = {
  global: {
    name: "Chief in Tech Global",
    tagline: "The Platform for Tech Leaders",
    icon: Globe,
    gradient: "from-[#4C1481] to-[#14B8A6]",
    accentColor: "text-[#14B8A6]",
    fallbackIcon: "Globe",
  },
  capital: {
    name: "Chief in Tech Capital",
    tagline: "Backing AI-Enabled Enterprise Systems",
    icon: TrendingUp,
    gradient: "from-[#4C1481] to-[#19739a]",
    accentColor: "text-[#19739a]",
    fallbackIcon: "TrendingUp",
  },
  media: {
    name: "Chief in Tech Media",
    tagline: "Tech Leadership Intelligence",
    icon: Newspaper,
    gradient: "from-[#4C1481] via-[#4C1481] via-53% to-[#000000]",
    accentColor: "text-[#000000]",
    fallbackIcon: "Newspaper",
  },
  network: {
    name: "Chief in Tech & EWIT Network",
    tagline: "Where Leaders Connect",
    icon: Users,
    gradient: "from-[#4C1481] to-[#6366F1]",
    accentColor: "text-[#6366F1]",
    fallbackIcon: "Users",
  },
  summit: {
    name: "Chief in Tech Summit",
    tagline: "The Premier Tech Leadership Event",
    icon: Calendar,
    gradient: "from-[#4C1481] to-[#110891]",
    accentColor: "text-[#110891]",
    fallbackIcon: "Calendar",
  },
  book: {
    name: "Chief in Tech Book",
    tagline: "The Path to Tech Leadership",
    icon: BookOpen,
    gradient: "from-[#4C1481] via-[#4C1481] via-59% to-[#D4AF37]",
    accentColor: "text-[#D4AF37]",
    fallbackIcon: "BookOpen",
  },
  chiefinai: {
    name: "Chief in AI Global",
    tagline: "Where AI Leaders Shape the Future",
    icon: Brain,
    gradient: "from-[#1a1a2e] to-[#4C1481]",
    accentColor: "text-[#4C1481]",
    fallbackIcon: "Brain",
  },
};

const sizeConfig = {
  sm: {
    icon: "w-4 h-4",
    text: "text-sm",
    tagline: "text-xs",
    iconContainer: "w-6 h-6",
  },
  md: {
    icon: "w-5 h-5",
    text: "text-base font-semibold",
    tagline: "text-xs",
    iconContainer: "w-8 h-8",
  },
  lg: {
    icon: "w-6 h-6",
    text: "text-lg font-semibold",
    tagline: "text-sm",
    iconContainer: "w-10 h-10",
  },
  xl: {
    icon: "w-8 h-8",
    text: "text-xl font-bold",
    tagline: "text-base",
    iconContainer: "w-12 h-12",
  },
};

export function ProductLogo({ 
  product, 
  variant = "full", 
  size = "md",
  className 
}: ProductLogoProps) {
  const { data: brands = [] } = useQuery<ProductBrand[]>({
    queryKey: ["/api/brands"],
    staleTime: 60000,
  });
  
  const defaultConfig = productConfig[product];
  const brand = brands.find(b => b.productKey === product);
  
  const config = {
    ...defaultConfig,
    name: product === "capital" ? defaultConfig.name : brand?.name || defaultConfig.name,
    tagline: product === "capital" ? defaultConfig.tagline : brand?.tagline || defaultConfig.tagline,
    gradient: brand?.gradient || defaultConfig.gradient,
    accentColor: brand?.accentColor || defaultConfig.accentColor,
  };

  // Prefer the server-injected logo map (rendered into the HTML shell on every
  // request) so cold-cache visits see the current admin-uploaded asset on the
  // first paint without flashing the bundled icon. Fall back to the brands
  // query result, then to the gradient + Lucide icon.
  const uploadedLogoUrl =
    getInjectedBrandLogo(product) ||
    brand?.logoUrl ||
    brand?.darkLogoUrl ||
    brand?.iconUrl ||
    null;

  const sizes = sizeConfig[size];
  const Icon = defaultConfig.icon;

  const iconBlock = uploadedLogoUrl ? (
    <div
      className={cn(
        "flex items-center justify-center overflow-hidden rounded-md",
        sizes.iconContainer,
      )}
    >
      <img
        src={uploadedLogoUrl}
        alt={config.name}
        className="h-full w-full object-contain"
        loading="lazy"
        decoding="async"
      />
    </div>
  ) : (
    <div
      className={cn(
        "flex items-center justify-center rounded-md text-white",
        sizes.iconContainer,
      )}
      style={gradientToInlineStyle(config.gradient)}
    >
      <Icon className={sizes.icon} />
    </div>
  );

  if (variant === "icon") {
    return (
      <div
        className={cn(className)}
        data-testid={`logo-icon-${product}`}
      >
        {iconBlock}
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <div 
        className={cn("flex items-center gap-2", className)}
        data-testid={`logo-compact-${product}`}
      >
        {iconBlock}
        <span className={cn("font-semibold", sizes.text)}>
          {config.name.replace("Chief in Tech ", "")}
        </span>
      </div>
    );
  }

  return (
    <div 
      className={cn("flex items-center gap-3", className)}
      data-testid={`logo-full-${product}`}
    >
      {iconBlock}
      <div className="flex flex-col">
        <span className={cn(sizes.text)}>{config.name}</span>
        <span className={cn("text-muted-foreground", sizes.tagline)}>
          {config.tagline}
        </span>
      </div>
    </div>
  );
}

export function ProductLogoGrid({ 
  products = ["capital", "media", "network", "summit", "book"] as ProductLine[],
  variant = "compact",
  size = "md",
  className
}: {
  products?: ProductLine[];
  variant?: "full" | "icon" | "compact";
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}) {
  return (
    <div 
      className={cn(
        "grid gap-4",
        products.length <= 3 ? "grid-cols-3" : "grid-cols-5",
        className
      )}
      data-testid="logo-grid"
    >
      {products.map((product) => (
        <ProductLogo 
          key={product} 
          product={product} 
          variant={variant} 
          size={size} 
        />
      ))}
    </div>
  );
}

export function getProductConfig(product: ProductLine) {
  return productConfig[product];
}

export { productConfig };
